import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { HomiSettingsActions } from './homi-settings.actions';
import { CallState, LoadingState } from '@lyceo/shared/util-loading';
import { formatISO } from 'date-fns';
import { HomiSettings } from '@lyceo/shared/homi-settings/util-models';
import { AuthActions } from '@lyceo/shared/auth/data-access';

export const HOMI_SETTINGS_FEATURE_KEY: string = 'homi-settings';

export interface HomiSettingsState {
  settings: HomiSettings | undefined;
  settingsCallState: CallState;
  refreshCallState: CallState;
  syncCallState: CallState;
}

export const initialHomiSettingsState: HomiSettingsState = {
  settings: undefined,
  settingsCallState: LoadingState.INIT,
  refreshCallState: LoadingState.INIT,
  syncCallState: LoadingState.INIT,
};

const reducer: ActionReducer<HomiSettingsState, Action> = createReducer(
  initialHomiSettingsState,
  on(
    AuthActions.logoutSuccess,
    (): HomiSettingsState => initialHomiSettingsState,
  ),
  on(
    HomiSettingsActions.refreshSettings,
    (state): HomiSettingsState => ({
      ...state,
      refreshCallState: LoadingState.LOADING,
    }),
  ),
  on(
    HomiSettingsActions.refreshSettingsSuccess,
    (state, action): HomiSettingsState => ({
      ...state,
      settings: action.settings,
      refreshCallState: LoadingState.LOADED,
    }),
  ),
  on(
    HomiSettingsActions.refreshSettingsFailure,
    (state, { error }): HomiSettingsState => ({
      ...state,
      refreshCallState: { errorMsg: error.message },
    }),
  ),
  on(
    HomiSettingsActions.getSettings,
    HomiSettingsActions.getSettingsByPupilId,
    (state): HomiSettingsState => ({
      ...state,
      settingsCallState: LoadingState.LOADING,
    }),
  ),
  on(
    HomiSettingsActions.getSettingsSuccess,
    (state, action): HomiSettingsState => ({
      ...state,
      settings: action.settings,
      settingsCallState: LoadingState.LOADED,
    }),
  ),
  on(
    HomiSettingsActions.getSettingsFailure,
    (state, { error }): HomiSettingsState => ({
      ...state,
      settingsCallState: { errorMsg: error.message },
    }),
  ),
  on(
    HomiSettingsActions.syncElo,
    (state): HomiSettingsState => ({
      ...state,
      syncCallState: LoadingState.LOADING,
    }),
  ),
  on(
    HomiSettingsActions.syncEloSuccess,
    (state): HomiSettingsState => ({
      ...state,
      syncCallState: LoadingState.LOADED,
      settings: state.settings
        ? {
            ...state.settings,
            elo: state.settings.elo
              ? {
                  ...state.settings.elo,
                  activitiesAndTasksSyncLastSuccessfulAt: formatISO(new Date()),
                }
              : undefined,
          }
        : undefined,
    }),
  ),
  on(
    HomiSettingsActions.syncEloFailure,
    (state, { error }): HomiSettingsState => ({
      ...state,
      syncCallState: { errorMsg: error.message },
    }),
  ),
);

export function homiSettingsReducer(
  state: HomiSettingsState | undefined,
  action: Action,
): HomiSettingsState {
  return reducer(state, action);
}
